"use client";

import * as React from "react";
import { cn } from "@/utils/utils";
import { styleDrivers } from "@/utils/componentStyle";

export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Variant of skeleton shape */
  variant?: "default" | "text" | "circular" | "rectangular";
  /** Width of the skeleton (CSS value) */
  width?: string | number;
  /** Height of the skeleton (CSS value) */
  height?: string | number;
  /** Whether to show animation */
  animate?: boolean;
}

/**
 * Skeleton displays a placeholder while content is loading.
 * Use it to reduce perceived loading time and prevent layout shift.
 */
function Skeleton({
  className,
  variant = "default",
  width,
  height,
  animate = true,
  style,
  ...props
}: SkeletonProps) {
  const variantClasses = {
    default: "",
    text: "h-4 w-full rounded",
    circular: "rounded-full aspect-square",
    rectangular: "rounded-md",
  };

  return (
    <div
      data-slot="skeleton"
      className={cn(
        styleDrivers.skeleton.root,
        !animate && "animate-none",
        variantClasses[variant],
        className
      )}
      style={{
        width: typeof width === "number" ? `${width}px` : width,
        height: typeof height === "number" ? `${height}px` : height,
        ...style,
      }}
      {...props}
    />
  );
}

Skeleton.displayName = "Skeleton";

/**
 * SkeletonText renders multiple text lines as skeletons.
 */
export interface SkeletonTextProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Number of lines to render */
  lines?: number;
  /** Gap between lines */
  gap?: number;
  /** Width of the last line (percentage) */
  lastLineWidth?: number;
}

function SkeletonText({
  className,
  lines = 3,
  gap = 8,
  lastLineWidth = 60,
  ...props
}: SkeletonTextProps) {
  return (
    <div
      className={cn("flex flex-col", className)}
      style={{ gap: `${gap}px` }}
      {...props}
    >
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          variant="text"
          style={{
            width: i === lines - 1 ? `${lastLineWidth}%` : "100%",
          }}
        />
      ))}
    </div>
  );
}

SkeletonText.displayName = "SkeletonText";

/**
 * SkeletonAvatar renders an avatar-shaped skeleton.
 */
export interface SkeletonAvatarProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Size of the avatar */
  size?: "sm" | "md" | "lg" | "xl";
}

function SkeletonAvatar({
  className,
  size = "md",
  ...props
}: SkeletonAvatarProps) {
  const sizeClasses = {
    sm: "h-8 w-8",
    md: "h-10 w-10",
    lg: "h-12 w-12",
    xl: "h-16 w-16",
  };

  return (
    <Skeleton
      variant="circular"
      className={cn(sizeClasses[size], className)}
      {...props}
    />
  );
}

SkeletonAvatar.displayName = "SkeletonAvatar";

/**
 * SkeletonCard renders a card-shaped skeleton with optional header.
 */
export interface SkeletonCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Show header section */
  showHeader?: boolean;
  /** Show footer section */
  showFooter?: boolean;
  /** Height of the content area */
  contentHeight?: number;
}

function SkeletonCard({
  className,
  showHeader = true,
  showFooter = false,
  contentHeight = 120,
  ...props
}: SkeletonCardProps) {
  return (
    <div
      className={cn(
        "rounded-lg border bg-card p-4 space-y-4",
        className
      )}
      {...props}
    >
      {showHeader && (
        <div className="flex items-center gap-3">
          <SkeletonAvatar size="md" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-24" />
          </div>
        </div>
      )}
      <Skeleton
        variant="rectangular"
        style={{ height: `${contentHeight}px` }}
        className="w-full"
      />
      {showFooter && (
        <div className="flex justify-between items-center">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-8 w-24 rounded-md" />
        </div>
      )}
    </div>
  );
}

SkeletonCard.displayName = "SkeletonCard";

/**
 * SkeletonTableRow renders a table row skeleton.
 */
export interface SkeletonTableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  /** Number of columns */
  columns?: number;
  /** Whether to show checkbox column */
  showCheckbox?: boolean;
}

function SkeletonTableRow({
  className,
  columns = 4,
  showCheckbox = false,
  ...props
}: SkeletonTableRowProps) {
  const totalColumns = showCheckbox ? columns + 1 : columns;

  return (
    <tr className={cn("border-b", className)} {...props}>
      {showCheckbox && (
        <td className="p-4">
          <Skeleton className="h-4 w-4" />
        </td>
      )}
      {Array.from({ length: columns }).map((_, i) => (
        <td key={i} className="p-4">
          <Skeleton
            className="h-4"
            style={{
              width: i === 0 ? "60%" : i === columns - 1 ? "40%" : "80%",
            }}
          />
        </td>
      ))}
    </tr>
  );
}

SkeletonTableRow.displayName = "SkeletonTableRow";

/**
 * SkeletonTable renders a complete table skeleton.
 */
export interface SkeletonTableProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Number of rows */
  rows?: number;
  /** Number of columns */
  columns?: number;
  /** Whether to show checkbox column */
  showCheckbox?: boolean;
  /** Whether to show header */
  showHeader?: boolean;
}

function SkeletonTable({
  className,
  rows = 5,
  columns = 4,
  showCheckbox = false,
  showHeader = true,
  ...props
}: SkeletonTableProps) {
  return (
    <div className={cn("w-full", className)} {...props}>
      <table className="w-full">
        {showHeader && (
          <thead>
            <tr className="border-b bg-muted/50">
              {showCheckbox && (
                <th className="p-4 text-left">
                  <Skeleton className="h-4 w-4" />
                </th>
              )}
              {Array.from({ length: columns }).map((_, i) => (
                <th key={i} className="p-4 text-left">
                  <Skeleton className="h-4 w-20" />
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {Array.from({ length: rows }).map((_, i) => (
            <SkeletonTableRow
              key={i}
              columns={columns}
              showCheckbox={showCheckbox}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

SkeletonTable.displayName = "SkeletonTable";

/**
 * SkeletonList renders a list of skeleton items.
 */
export interface SkeletonListProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Number of items */
  items?: number;
  /** Whether to show avatar */
  showAvatar?: boolean;
  /** Whether to show secondary text */
  showSecondaryText?: boolean;
}

function SkeletonList({
  className,
  items = 5,
  showAvatar = true,
  showSecondaryText = true,
  ...props
}: SkeletonListProps) {
  return (
    <div className={cn("space-y-4", className)} {...props}>
      {Array.from({ length: items }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          {showAvatar && <SkeletonAvatar size="md" />}
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-3/4" />
            {showSecondaryText && <Skeleton className="h-3 w-1/2" />}
          </div>
        </div>
      ))}
    </div>
  );
}

SkeletonList.displayName = "SkeletonList";

export {
  Skeleton,
  SkeletonText,
  SkeletonAvatar,
  SkeletonCard,
  SkeletonTableRow,
  SkeletonTable,
  SkeletonList,
};
